/*
 * Copyright 2026 Sony Corporation
 */

import { ColorCurves } from '@babylonjs/core/Materials/colorCurves';
// The JPEG encoder uploads the pixels as a raw texture.
import '@babylonjs/core/Engines/Extensions/engine.rawTexture';
import { DumpDataAsync } from '@babylonjs/core/Misc/dumpTools';
import { ImageProcessingConfiguration } from '@babylonjs/core/Materials/imageProcessingConfiguration';
import { ImageProcessingPostProcess } from '@babylonjs/core/PostProcesses/imageProcessingPostProcess';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { SharpenPostProcess } from '@babylonjs/core/PostProcesses/sharpenPostProcess';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { CreativeLook } from '@ips-device-verse/twin-service/schema';

import { aspectOf, fieldOfView, type ResolvedOptics } from './camera-optics';
import { applyLook } from './camera-preview';
import { SCENE_LAYER } from './layers';
import type { SceneEditor } from './scene-editor';
import { splatsSortedFor } from './splat';
import type { Transform } from './types';

/**
 * Wide enough for a picture worth inspecting, small enough to render, encode
 * and upload inside the few seconds a PTP client waits for its shot.
 */
export const STILL_MAX_WIDTH = 1920;

const JPEG_QUALITY = 0.9;

/** Past this the gateway has given up on the shot and sent its test pattern. */
const READY_TIMEOUT_MS = 3000;

const READY_POLL_MS = 16;

const waitUntil = async (ready: () => boolean, timeoutMs: number) => {
  const deadline = performance.now() + timeoutMs;
  while (!ready()) {
    if (performance.now() > deadline) {
      throw new Error('still: scene not ready in time');
    }
    await new Promise((resolve) => setTimeout(resolve, READY_POLL_MS));
  }
};

/**
 * What a virtual camera's shutter captures: its view of the world, through its
 * lens, at its format's aspect, with its Creative Look applied.
 *
 * Rendered off screen with a camera of its own, so the viewport and the preview
 * are untouched and any device can be shot, not only the selected one. Depth of
 * field is left out: it is the preview's most expensive pass and a still has to
 * be ready in seconds. Returns the JPEG as base64, ready for the API.
 */
export const renderStill = async (
  editor: SceneEditor,
  transform: Transform,
  optics: ResolvedOptics,
  look: Required<CreativeLook>
): Promise<string> => {
  const { position, rotation } = transform;
  const camera = new TargetCamera('StillCamera', new Vector3(position.x, position.y, position.z), editor.scene, false);
  camera.rotation = new Vector3(rotation.x, rotation.y, rotation.z);
  camera.fov = fieldOfView(optics).vertical;
  camera.layerMask = SCENE_LAYER;
  const { radius } = editor.activeBounds();
  camera.minZ = radius * 0.01;
  camera.maxZ = radius * 200;

  // The same passes as the preview, so the still is what the inspector shows.
  const grading = new ImageProcessingConfiguration();
  grading.colorCurvesEnabled = true;
  grading.colorCurves = new ColorCurves();
  const sharpen = new SharpenPostProcess('StillSharpen', 1, camera);
  sharpen.colorAmount = 1;
  const grade = new ImageProcessingPostProcess('StillGrade', 1, camera);
  grade.imageProcessingConfiguration = grading;
  // Materials already wrote gamma-space colour; encoding it again washes it out.
  grade.fromLinearSpace = false;
  applyLook(grading, sharpen, look);

  const width = Math.min(optics.format.width_px, STILL_MAX_WIDTH);
  const height = Math.round(width / aspectOf(optics));
  const { scene, engine } = editor;
  // Multisampled rather than FXAA: the FXAA shader's first compile alone costs
  // seconds, which is the whole budget.
  const texture = new RenderTargetTexture('StillTarget', { width, height }, scene, { samples: 4 });
  texture.renderList = scene.meshes.slice();
  texture.activeCamera = camera;
  texture.forceLayerMaskCheck = true;

  try {
    await waitUntil(
      () => texture.isReadyForRendering() && camera.isReady(true) && splatsSortedFor(camera, scene.meshes),
      READY_TIMEOUT_MS
    );

    // Rendered here and now rather than on the next frame, which is what
    // Babylon's screenshot helper waits for: a tab in the background gets no
    // frames, and its answer would never come.
    const { activeCamera, activeCameras } = scene;
    const { getRenderWidth, getRenderHeight } = engine;
    // Post-processes size themselves from the engine, not the target.
    engine.getRenderWidth = () => width;
    engine.getRenderHeight = () => height;
    scene.activeCamera = camera;
    scene.activeCameras = null;
    camera.outputRenderTarget = texture;
    try {
      scene.incrementRenderId();
      scene.resetCachedMaterial();
      scene.render();
    } finally {
      scene.activeCamera = activeCamera;
      scene.activeCameras = activeCameras;
      camera.outputRenderTarget = null;
      engine.getRenderWidth = getRenderWidth;
      engine.getRenderHeight = getRenderHeight;
    }

    const pixels = await texture.readPixels(undefined, undefined, undefined, false);
    if (!pixels) {
      throw new Error('still: render target could not be read');
    }
    const dataUrl = await DumpDataAsync(width, height, pixels, 'image/jpeg', undefined, true, false, JPEG_QUALITY);
    if (typeof dataUrl !== 'string') {
      throw new Error('still: encoder returned no image');
    }
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  } finally {
    texture.dispose();
    sharpen.dispose(camera);
    grade.dispose(camera);
    camera.dispose();
  }
};
